/*! Copyright (c) Safe As Milk. All rights reserved. */
import Cart from "cart-store";
import { reconcileDepositItems } from "cart-deposit-sync";

function getDepositConfig() {
    const depositData = document.getElementById("cart-deposit-data");
    if (!depositData) {
        return {
            productId: null,
            variantsByAmount: {}
        };
    }
    try {
        return JSON.parse(depositData.textContent);
    } catch (_error) {
        return {
            productId: null,
            variantsByAmount: {}
        };
    }
}

function formatDepositAmount(cents) {
    return new Intl.NumberFormat("nl-NL", {
        style: "currency",
        currency: "EUR"
    }).format(cents / 100);
}

function createDepositGroupId() {
    return `deposit-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// --- Bestaande statiegeld-groep hergebruiken --------------------------------
//
// Shopify behandelt twee line items met dezelfde variant maar verschillende
// line-item-properties als aparte regels. Omdat we bij élke toevoeging een
// uniek _deposit_group_id genereerden, leverde het tweemaal toevoegen van
// hetzelfde statiegeld-product twee losse regels op in plaats van één regel met
// een opgehoogd aantal.
//
// Oplossing: vóór we een nieuw group-id aanmaken kijken of er al een main-line
// in de winkelmand zit met exact dezelfde variant, hetzelfde statiegeld-bedrag
// én dezelfde overige (klant-)properties. Zo ja → hergebruik dat group-id,
// zodat de nieuwe Cart.add samenvalt met de bestaande regel (main + deposit
// allebei) en het aantal netjes ophoogt.

function getCartItemProperty(item, property) {
    return item?.properties?.[property] || null;
}

// Klant-zichtbare properties uit de form-data: alles onder properties[...] dat
// niet met een underscore begint (underscore-properties zijn intern).
function getCustomerPropertiesFromForm(formItem) {
    const props = {};
    for (const [key, value] of Object.entries(formItem)) {
        const match = /^properties\[(.+)\]$/.exec(key);
        if (!match) continue;
        const name = match[1];
        if (name.charAt(0) === "_") continue;
        props[name] = String(value);
    }
    return props;
}

// Klant-zichtbare properties van een bestaande cart-line.
function getCustomerPropertiesFromCartItem(cartItem) {
    const props = {};
    const all = cartItem?.properties || {};
    for (const [name, value] of Object.entries(all)) {
        if (name.charAt(0) === "_") continue;
        props[name] = String(value);
    }
    return props;
}

function shallowEqualStringMaps(a, b) {
    const aKeys = Object.keys(a);
    if (aKeys.length !== Object.keys(b).length) return false;
    return aKeys.every((key => a[key] === b[key]));
}

// Zoekt een bestaande main-line voor exact deze variant + dit statiegeld-bedrag
// + dezelfde klant-properties + hetzelfde selling plan. Geeft het bijbehorende
// _deposit_group_id terug, of null als er nog niets matchends in de mand zit.
// Alleen bij een volledige match hergebruiken we het group-id — anders zouden
// twee inhoudelijk verschillende regels per ongeluk samengevoegd worden.
function findReusableDepositGroupId({variantId: variantId, depositCents: depositCents, formItem: formItem}) {
    const cartItems = Cart.getState().cart?.items || [];
    const numericVariantId = Number(variantId);
    const formProps = getCustomerPropertiesFromForm(formItem);
    const formSellingPlan = formItem.selling_plan ? String(formItem.selling_plan) : "";
    for (const cartItem of cartItems) {
        if (Number(cartItem.variant_id) !== numericVariantId) continue;
        if (getCartItemProperty(cartItem, "_deposit_role") !== "main") continue;
        if (Number(getCartItemProperty(cartItem, "_deposit_cents")) !== depositCents) continue;
        const groupId = getCartItemProperty(cartItem, "_deposit_group_id");
        if (!groupId) continue;
        const cartSellingPlan = cartItem.selling_plan_allocation?.selling_plan?.id != null ? String(cartItem.selling_plan_allocation.selling_plan.id) : "";
        if (cartSellingPlan !== formSellingPlan) continue;
        if (!shallowEqualStringMaps(formProps, getCustomerPropertiesFromCartItem(cartItem))) continue;
        return groupId;
    }
    return null;
}

// --- Pre-flight inventory check ---------------------------------------------
//
// We halen de canonieke variant-data live op via /products/{handle}.js zodat we
// niet vertrouwen op door de CDN gecachete Liquid-data. Resultaten worden kort
// gecachet (CACHE_TTL_MS) zodat snel achter elkaar toevoegen niet onnodig veel
// requests veroorzaakt.
const PRODUCT_CACHE_TTL_MS = 5e3;

const productCache = new Map;

async function fetchProductByHandle(handle) {
    if (!handle) return null;
    const now = Date.now();
    const cached = productCache.get(handle);
    if (cached && now - cached.fetchedAt < PRODUCT_CACHE_TTL_MS) {
        return cached.product;
    }
    const rootRoute = window.Shopify?.routes?.root ?? "/";
    const url = `${rootRoute}products/${handle}.js`;
    const response = await fetch(url, {
        headers: {
            Accept: "application/json"
        }
    });
    if (!response.ok) {
        throw new Error(`Kon productinformatie niet ophalen (${response.status}).`);
    }
    const product = await response.json();
    productCache.set(handle, {
        product: product,
        fetchedAt: now
    });
    return product;
}

function findVariant(product, variantId) {
    if (!product || !Array.isArray(product.variants)) return null;
    const numericId = Number(variantId);
    return product.variants.find((v => Number(v.id) === numericId)) || null;
}

function isVariantInventoryCapped(variant) {
    if (!variant) return false;
    // inventory_management is null wanneer voorraad niet wordt bijgehouden.
    if (!variant.inventory_management) return false;
    // 'continue' = oversell toegestaan → geen cap.
    if (variant.inventory_policy === "continue") return false;
    return true;
}

function getQuantityAlreadyInCart(variantId) {
    const cartState = Cart.getState().cart;
    const items = cartState?.items || [];
    const numericId = Number(variantId);
    return items.reduce(((sum, item) => Number(item.variant_id) === numericId ? sum + Number(item.quantity || 0) : sum), 0);
}

async function determineEffectiveQuantity({handle: handle, variantId: variantId, requested: requested}) {
    const passthrough = {
        quantity: requested,
        capped: false,
        available: null
    };
    if (!handle) return passthrough;
    let product;
    try {
        product = await fetchProductByHandle(handle);
    } catch (error) {
        console.warn("[deposit] Pre-flight voorraadcheck faalde, doorgaan zonder cap:", error);
        return passthrough;
    }
    const variant = findVariant(product, variantId);
    if (!variant) return passthrough;
    if (!isVariantInventoryCapped(variant)) return passthrough;
    // inventory_quantity ontbreekt in /products/{handle}.js bij sommige
    // Shopify-instellingen (privacy / 'show inventory'). Niet aannemen dat
    // het 0 is — laat Shopify in dat geval beslissen.
    const rawInv = variant.inventory_quantity;
    if (typeof rawInv !== "number" || !Number.isFinite(rawInv)) {
        return passthrough;
    }
    const totalAvailable = Math.max(0, rawInv);
    const alreadyInCart = getQuantityAlreadyInCart(variantId);
    const remaining = totalAvailable - alreadyInCart;
    if (remaining >= requested) {
        return {
            quantity: requested,
            capped: false,
            available: remaining
        };
    }
    if (remaining > 0) {
        // Cap naar wat nog beschikbaar is — main én deposit krijgen straks
        // hetzelfde aantal mee, dus geen mismatch.
        return {
            quantity: remaining,
            capped: true,
            available: remaining
        };
    }
    // Volgens onze (mogelijk gecachete) data is er niets meer beschikbaar.
    // Geen reden om de klant te blokkeren — laat Shopify het laatste woord
    // hebben. Bij echte stock-uitputting wijst Shopify de bulk-add atomair af
    // en valt de actie via het bestaande error-pad netjes terug.
    return passthrough;
}

class ProductForm extends HTMLElement {
    #boundSubmit;
    #eventAttached;
    static #formsBeingProcessed=[];
    constructor() {
        super();
        this.#boundSubmit = this.submit.bind(this);
    }
    connectedCallback() {
        this.#eventAttached = false;
        setTimeout((() => {
            this.form = this.querySelector("form");
            this.idInput = this.form.querySelector('input[name="id"]');
            this.submitButton = this.form.elements.add;
            this.stagedAction = this.querySelector("staged-action");
            this.idInput.removeAttribute("disabled");
            this.notifyOnAdd = this.hasAttribute("notify-on-add");
            this.redirectOnAdd = this.getAttribute("redirect-on-add");
            this.#eventAttached = true;
            this.form.addEventListener("submit", this.#boundSubmit);
        }));
    }
    disconnectedCallback() {
        if (this.#eventAttached) {
            this.#eventAttached = false;
            this.form.removeEventListener("submit", this.#boundSubmit);
        }
    }
    async submit(e) {
        e.preventDefault();
        const data = new FormData(e.target);
        const item = Object.fromEntries(data.entries());
        const depositCents = Number(this.dataset.depositCents || 0);
        const requestedQuantity = Number(item.quantity || 1);
        const productHandle = this.dataset.productHandle || null;
        let depositVariantId = null;
        try {
            this.removeError();
            if (this.redirectOnAdd && !ProductForm.#formsBeingProcessed.includes(this.form.id)) {
                ProductForm.#formsBeingProcessed.push(this.form.id);
            }
            if (this.stagedAction) await this.stagedAction.setState("DOING");
            // Pre-flight: cap quantity op werkelijke voorraad zodat de
            // statiegeld-line niet uit sync raakt door een server-cap. Pre-flight
            // is puur adviserend — als hij niets nuttigs kan zeggen valt hij
            // terug op de gevraagde quantity en laat Shopify het beslissen.
            const effective = await determineEffectiveQuantity({
                handle: productHandle,
                variantId: item.id,
                requested: requestedQuantity
            });
            const quantity = Math.max(1, Number(effective.quantity) || requestedQuantity);
            const itemsToAdd = [ {
                ...item,
                quantity: quantity
            } ];
            if (depositCents > 0) {
                const depositConfig = getDepositConfig();
                depositVariantId = depositConfig.variantsByAmount?.[depositCents.toString()];
                if (!depositVariantId) {
                    throw new Error(`Geen statiegeldvariant gevonden voor ${formatDepositAmount(depositCents)}.`);
                }
                // Hergebruik een bestaand group-id wanneer dit exacte product al
                // in de mand zit, zodat de regel ophoogt i.p.v. dupliceert.
                // Alleen als er niets matchends is maken we een nieuw group-id.
                const depositGroupId = findReusableDepositGroupId({
                    variantId: item.id,
                    depositCents: depositCents,
                    formItem: item
                }) || createDepositGroupId();
                itemsToAdd[0]["properties[_deposit_group_id]"] = depositGroupId;
                itemsToAdd[0]["properties[_deposit_role]"] = "main";
                // _deposit_cents op de main-line zodat de reconciler ook bij
                // bypass-flows (Shop Pay, /cart/add URLs, third-party apps)
                // weet welke statiegeldvariant erbij hoort.
                itemsToAdd[0]["properties[_deposit_cents]"] = String(depositCents);
                itemsToAdd.push({
                    id: depositVariantId,
                    quantity: quantity,
                    "properties[_deposit_group_id]": depositGroupId,
                    "properties[_deposit_role]": "deposit",
                    "properties[_deposit_cents]": String(depositCents),
                    "properties[Type]": "Statiegeld",
                    "properties[Voor]": this.dataset.productTitle || "Product"
                });
            }
            await Cart.add(itemsToAdd);
            // Wacht tot de statiegeld-reconciler klaar is vóórdat we de cart
            // drawer of notification openen. Anders ziet de klant (en Shop Pay
            // / Apple Pay express checkout!) een tussentoestand waarin Shopify
            // het hoofdproduct heeft gecap't maar de statiegeld-line nog niet
            // is bijgewerkt.
            try {
                await reconcileDepositItems();
            } catch (reconcileError) {
                console.warn("[deposit] Reconcile na add faalde:", reconcileError);
            }
            if (effective.capped) {
                this.#renderNotice(`Slechts ${effective.available} op voorraad — aantal aangepast naar ${quantity}.`);
            }
            if (this.redirectOnAdd) {
                ProductForm.#formsBeingProcessed = ProductForm.#formsBeingProcessed.filter((id => id !== this.form.id));
                if (ProductForm.#formsBeingProcessed.length === 0) {
                    window.location.href = this.redirectOnAdd;
                }
            }
            if (this.stagedAction) await this.stagedAction.setState("DONE");
            const quickShop = this.closest("quick-shop");
            if (quickShop) {
                await quickShop.close();
            }
            if (this.notifyOnAdd) {
                await Cart.resetVariantsBeingAdded();
                if (!this.closest(".modal--cart")) {
                    const cart = document.querySelector(".modal--cart, .popup--cart-notification");
                    if (cart && !cart.shown) {
                        cart.open();
                    } else if (cart && cart.interval) {
                        cart.interval.reset();
                        cart.interval.start();
                    }
                }
            } else {
                await Cart.resetLatestAddedProduct();
                if (depositVariantId) {
                    await Cart.resetVariantsBeingAdded();
                } else {
                    await Cart.resetVariantsBeingAdded(item.id);
                }
            }
        } catch (error) {
            this.#renderError(error.message);
            if (this.stagedAction) await this.stagedAction.setState("IDLE");
            if (this.redirectOnAdd) {
                ProductForm.#formsBeingProcessed = ProductForm.#formsBeingProcessed.filter((id => id !== this.form.id));
            }
        }
    }
    removeError() {
        const error = this.querySelector(".errors");
        if (!error) return;
        error.remove();
    }
    #renderError(message) {
        this.removeError();
        if (this.submitButton) {
            const error = document.createElement("div");
            error.classList.add("errors", "qty-error", "u-small");
            error.innerHTML = message;
            this.submitButton.before(error);
        }
    }
    #renderNotice(message) {
        // Zelfde DOM-positie als #renderError zodat bestaande styling werkt,
        // maar met .qty-notice zodat we ze later eventueel los kunnen stylen.
        this.removeError();
        if (this.submitButton) {
            const notice = document.createElement("div");
            notice.classList.add("errors", "qty-error", "qty-notice", "u-small");
            notice.innerHTML = message;
            this.submitButton.before(notice);
        }
    }
}

customElements.define("product-form", ProductForm);
//# sourceMappingURL=product-form.js.map
